import { loadSession } from "./session.js";
import { buildId } from "./build-id.js";

/**
 * Client telemetry reporter. POSTs unhandled errors and onboarding funnel events
 * to the api so post-login / onboarding failures and drop-off are visible in
 * /app/admin instead of dying silently in the user's console.
 *
 * Fire-and-forget: uses keepalive so it survives navigation/unload, and never
 * throws (telemetry must not break the page that is already broken). No PII is
 * sent: just kind, message/stack, route, a signed_in flag, and the user agent
 * (added server-side).
 */
const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

type Kind = "error" | "unhandledrejection" | "funnel";

/** Strip likely PII (emails, long digit runs) before a message/stack leaves the
 * client, since error text can incidentally echo user input. Best-effort. */
function redact(s?: string): string | undefined {
  if (!s) return s;
  return s
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]")
    .replace(/\b\d{6,}\b/g, "[num]");
}

function send(kind: Kind, data: { name?: string; message?: string; stack?: string }) {
  try {
    const body = JSON.stringify({
      kind,
      name: data.name,
      message: redact(data.message),
      stack: redact(data.stack),
      route: typeof location !== "undefined" ? location.pathname : undefined,
      signed_in: !!loadSession(),
      // Which build produced this. Without it a report that arrives AFTER its
      // fix ships is unreadable: it could be the fix failing or a tab still
      // running last week's bundle, and the only way to tell was to fetch the
      // deployed chunk and grep it by hand. See lib/build-id.ts.
      build: buildId(),
    });
    // Send the token when we have one so the server can attribute the report to
    // a user. The endpoint stays PUBLIC and stores NULL when it is absent or
    // stale: an error mid-onboarding or while logged out is exactly the report
    // most worth keeping. Without this, telemetry carries a device but no way to
    // tie it to the session it came from.
    const sess = loadSession();
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (sess?.session_id) headers.Authorization = `Bearer ${sess.session_id}`;
    void fetch(`${API_BASE}/telemetry`, {
      method: "POST",
      headers,
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* never throw from telemetry */
  }
}

/** Record an onboarding/funnel step (e.g. "onboarding_welcome", "reached_home"). */
export function reportFunnel(name: string) {
  send("funnel", { name });
}

/** Report a caught render error (called from ErrorBoundary). */
export function reportError(label: string | undefined, error: Error, componentStack?: string) {
  send("error", {
    name: label,
    message: error.message,
    stack: error.stack ?? componentStack,
  });
}

/**
 * True when an error came from a browser extension rather than from us.
 *
 * Extensions run in the page and their exceptions reach our global handler, but
 * they are not our code and we cannot fix them. One user's extension threw
 * "Cannot read properties of undefined (reading 'M_ID')" 426 times across five
 * routes in a few hours, which was 99% of the open error list and buried a real
 * report. Checking the STACK matters as much as the filename: for an extension
 * script window.onerror often has no filename at all, which is why an earlier
 * filename-only check missed every one of these.
 */
function isExtensionError(filename?: string, stack?: string): boolean {
  const hay = `${filename ?? ""} ${stack ?? ""}`;
  return /(chrome|moz|safari-web)-extension:\/\//.test(hay);
}

/** Host of a script URL, for naming third-party failures we cannot read. */
function scriptOrigin(filename?: string): string {
  if (!filename) return "unknown";
  try {
    return new URL(filename).host;
  } catch {
    return "unknown";
  }
}

let installed = false;

/** Install global handlers for uncaught errors + unhandled promise rejections. */
export function installGlobalErrorReporter() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (e) => {
    // Errors thrown inside a cross-origin script reach us as the bare string
    // "Script error." with no stack, because the browser withholds detail it
    // cannot attribute. All 13 of these in production came from the third-party
    // calculator we embed on practice tests. They are unreadable and unfixable
    // by us, and they were a third of the whole error list, so name them rather
    // than let them crowd out our own failures. They stay queryable.
    if (isExtensionError(e.filename, e.error?.stack)) return;
    const thirdParty =
      e.message === "Script error." ||
      (!!e.filename && !e.filename.startsWith(location.origin));
    send("error", {
      name: thirdParty ? `third_party:${scriptOrigin(e.filename)}` : undefined,
      message: e.message,
      stack: e.error?.stack ?? `${e.filename}:${e.lineno}:${e.colno}`,
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason as { message?: string; stack?: string } | undefined;
    // Only trust an Error-shaped reason's message; a non-Error reason could be
    // any value (an object, a typed answer) and is not safe to stringify.
    const message =
      typeof reason?.message === "string"
        ? reason.message
        : "Unhandled promise rejection (non-Error reason)";
    // "The database connection is closing" was filtered here on 2026-09-01 as
    // harmless teardown noise, "none actionable". That was wrong. Every one of
    // those rejections was the pack cache holding a CLOSED IndexedDB handle: the
    // offline drill cache was dead for the life of the page, and the fix landed
    // on 2026-09-08 (lib/pack-cache.ts, lib/offline-queue.ts). Suppressing the
    // symptom of a bug we had not diagnosed cost a week, and would now hide the
    // one signal that says whether the fix held. It reports again, and the
    // build field distinguishes a stale tab from a live regression.
    // Same extension traffic arrives here too, with no filename to test.
    if (isExtensionError(undefined, reason?.stack)) return;
    send("unhandledrejection", { message, stack: reason?.stack });
  });
}
